/**
 * LaserRouteOverlay - Cyberpunk laser trace animation for caliche routes
 * 
 * Features:
 * - Animated laser trace that follows route waypoints
 * - Glowing trail effect that fades behind
 * - Waypoint markers that pulse when laser passes
 * - Facility-type colored lasers (Well=amber, SWD=emerald, Rig=yellow)
 * - Dark/Light mode adaptive glow
 * - Continuous loop with random 1-2 second pause
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { useAppStore } from '../stores/appStore';

// Hit box for clickable distance labels
interface LabelHitBox {
  routeId: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

// Internal waypoint type used in processed routes
interface Waypoint {
  lat: number;
  lng: number;
  sequence: number;
}

interface Landmark {
  latitude: number;
  longitude: number;
  sequence_order: number;
  landmark_type?: string;
}

// Raw route data from API
interface RawCalichemRoute {
  route_id: string;
  name: string;
  surface_type?: 'paved' | 'caliche' | 'dirt' | 'gravel' | 'mixed' | string;
  landmarks: Landmark[];
  facility_id?: string;
}

// Internal processed route
interface ProcessedRoute {
  id: string;
  name: string;
  surfaceType: string;
  waypoints: { lat: number; lng: number; sequence: number }[];
  distanceMiles: number; // Total route distance in miles
}

// Exported type for route selection callback
export interface SelectedRouteInfo {
  id: string;
  name: string;
  distanceMiles: number;
  waypoints: { lat: number; lng: number }[];
  surfaceType: string;
}

interface LaserRouteOverlayProps {
  map: google.maps.Map | null;
  routes: RawCalichemRoute[];
  visible: boolean;
  onRouteSelect?: (route: SelectedRouteInfo | null) => void;
}

// Surface type colors - cyberpunk aesthetic
const SURFACE_COLORS = {
  paved: { dark: '#22c55e', light: '#16a34a' },     // Green
  caliche: { dark: '#f59e0b', light: '#d97706' },   // Amber
  dirt: { dark: '#a16207', light: '#92400e' },       // Brown
  gravel: { dark: '#94a3b8', light: '#64748b' },     // Slate
  mixed: { dark: '#8b5cf6', light: '#7c3aed' },      // Purple
  default: { dark: '#60a5fa', light: '#3b82f6' },    // Blue
};

// Unique colors for routes that cross other paths - cyberpunk neon palette
const CROSSING_COLORS = [
  { dark: '#ff006e', light: '#e91e63' },  // Hot Pink
  { dark: '#00f5d4', light: '#00bcd4' },  // Cyan
  { dark: '#fee440', light: '#ffc107' },  // Yellow
  { dark: '#9b5de5', light: '#9c27b0' },  // Purple
  { dark: '#00bbf9', light: '#2196f3' },  // Electric Blue
  { dark: '#f15bb5', light: '#e91e63' },  // Magenta
  { dark: '#00ff87', light: '#4caf50' },  // Neon Green
  { dark: '#ff5400', light: '#ff5722' },  // Orange
  { dark: '#7b2cbf', light: '#673ab7' },  // Deep Purple
  { dark: '#4cc9f0', light: '#03a9f4' },  // Sky Blue
];

// Animation settings
const ANIMATION_SETTINGS = {
  baseSpeed: 0.012, // FAST: 4x original speed for snappy race animation
  glowIntensity: { dark: 20, light: 12 },
  coreWidth: 6,
  trailLength: 0.12, // Slightly shorter trail for faster movement
  waypointPulseScale: 1.5,
  waypointPulseDuration: 400, // ms - faster pulse
  pauseMin: 1800, // 1.8 second min pause 
  pauseMax: 2500, // 2.5 second max pause
  fadeOutDuration: 600, // ms - faster fade
  dimmedOpacity: 0.2, // Opacity for non-isolated routes when one is focused
};

// Haversine formula to calculate distance between two lat/lng points in miles
const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 3958.8; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const LaserRouteOverlay: React.FC<LaserRouteOverlayProps> = ({
  map,
  routes,
  visible,
  onRouteSelect,
}) => {
  const darkMode = useAppStore((state) => state.darkMode);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const routeStatesRef = useRef<Map<string, RouteAnimationState>>(new Map());
  const routeColorsRef = useRef<Map<string, string>>(new Map()); // Assigned colors for crossing routes
  const labelHitBoxesRef = useRef<LabelHitBox[]>([]); // Click targets for distance labels
  const shortestDistanceRef = useRef<number>(0); // Shortest route distance for race mode
  const processedRoutesRef = useRef<ProcessedRoute[]>([]); // Store processed routes for selection lookup
  
  // State for isolating a single route when clicked
  const [isolatedRouteId, setIsolatedRouteId] = useState<string | null>(null);

  interface RouteAnimationState {
    progress: number; // 0 to 1
    phase: 'tracing' | 'pausing' | 'fading';
    pauseEndTime: number;
    fadeProgress: number;
    activatedWaypoints: Set<number>;
    waypointPulses: Map<number, number>; // waypoint index -> pulse start time
  }

  // Notify parent when route selection changes
  useEffect(() => {
    if (!onRouteSelect) return;
    
    if (isolatedRouteId === null) {
      onRouteSelect(null);
    } else {
      const route = processedRoutesRef.current.find(r => r.id === isolatedRouteId);
      if (route) {
        onRouteSelect({
          id: route.id,
          name: route.name,
          distanceMiles: route.distanceMiles,
          waypoints: route.waypoints.map(w => ({ lat: w.lat, lng: w.lng })),
          surfaceType: route.surfaceType,
        });
      }
    }
  }, [isolatedRouteId, onRouteSelect]);

  // Check if two line segments intersect
  const segmentsIntersect = useCallback((
    p1: { lat: number; lng: number }, p2: { lat: number; lng: number },
    p3: { lat: number; lng: number }, p4: { lat: number; lng: number }
  ): boolean => {
    const ccw = (A: { lat: number; lng: number }, B: { lat: number; lng: number }, C: { lat: number; lng: number }) => {
      return (C.lng - A.lng) * (B.lat - A.lat) > (B.lng - A.lng) * (C.lat - A.lat);
    };
    return ccw(p1, p3, p4) !== ccw(p2, p3, p4) && ccw(p1, p2, p3) !== ccw(p1, p2, p4);
  }, []);

  // Check if a route crosses any other route
  const routeCrossesOthers = useCallback((
    route: ProcessedRoute,
    allRoutes: ProcessedRoute[]
  ): boolean => {
    for (const otherRoute of allRoutes) {
      if (otherRoute.id === route.id) continue;
      
      // Check each segment of route against each segment of otherRoute
      for (let i = 0; i < route.waypoints.length - 1; i++) {
        for (let j = 0; j < otherRoute.waypoints.length - 1; j++) {
          if (segmentsIntersect(
            route.waypoints[i], route.waypoints[i + 1],
            otherRoute.waypoints[j], otherRoute.waypoints[j + 1]
          )) {
            return true;
          }
        }
      }
    }
    return false;
  }, [segmentsIntersect]);

  // Assign unique colors to crossing routes
  const assignRouteColors = useCallback((processedRoutes: ProcessedRoute[]) => {
    let crossingColorIndex = 0;
    
    processedRoutes.forEach(route => {
      if (!routeColorsRef.current.has(route.id)) {
        if (routeCrossesOthers(route, processedRoutes)) {
          // Assign a unique crossing color
          const colorSet = CROSSING_COLORS[crossingColorIndex % CROSSING_COLORS.length];
          routeColorsRef.current.set(route.id, darkMode ? colorSet.dark : colorSet.light);
          crossingColorIndex++;
        } else {
          // Use surface type color
          const colors = SURFACE_COLORS[route.surfaceType as keyof typeof SURFACE_COLORS] || SURFACE_COLORS.default;
          routeColorsRef.current.set(route.id, darkMode ? colors.dark : colors.light);
        }
      }
    });
  }, [darkMode, routeCrossesOthers]);

  // Get color for a specific route (either crossing color or surface type)
  const getRouteColor = useCallback((routeId: string, surfaceType: string): string => {
    const assignedColor = routeColorsRef.current.get(routeId);
    if (assignedColor) return assignedColor;
    
    const colors = SURFACE_COLORS[surfaceType as keyof typeof SURFACE_COLORS] || SURFACE_COLORS.default;
    return darkMode ? colors.dark : colors.light;
  }, [darkMode]);

  // Transform raw route data to processed format
  const processRoutes = useCallback((rawRoutes: RawCalichemRoute[]): ProcessedRoute[] => {
    return rawRoutes
      .filter(route => route.landmarks && route.landmarks.length >= 2)
      .map(route => {
        const sortedLandmarks = [...route.landmarks].sort((a, b) => a.sequence_order - b.sequence_order);
        const waypoints = sortedLandmarks.map(lm => ({
          lat: lm.latitude,
          lng: lm.longitude,
          sequence: lm.sequence_order,
        }));
        
        // Calculate total distance in miles
        let distanceMiles = 0;
        for (let i = 1; i < waypoints.length; i++) {
          distanceMiles += haversineDistance(
            waypoints[i - 1].lat, waypoints[i - 1].lng,
            waypoints[i].lat, waypoints[i].lng
          );
        }
        
        return {
          id: route.route_id,
          name: route.name,
          surfaceType: route.surface_type || 'default',
          waypoints,
          distanceMiles,
        };
      });
  }, []);

  // Convert lat/lng to canvas coordinates
  const latLngToPixel = useCallback((lat: number, lng: number): { x: number; y: number } | null => {
    if (!map) return null;
    const projection = map.getProjection();
    if (!projection) return null;

    const bounds = map.getBounds();
    if (!bounds) return null;

    const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
    const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
    const point = projection.fromLatLngToPoint(new google.maps.LatLng(lat, lng));

    if (!topRight || !bottomLeft || !point) return null;

    const worldWidth = topRight.x - bottomLeft.x;
    
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const x = ((point.x - bottomLeft.x) / worldWidth) * canvas.width;
    const y = ((point.y - topRight.y) / (bottomLeft.y - topRight.y)) * canvas.height;

    return { x, y };
  }, [map]);

  // Calculate total path length in pixels
  const calculatePathLength = useCallback((waypoints: Waypoint[]): number => {
    let length = 0;
    for (let i = 1; i < waypoints.length; i++) {
      const p1 = latLngToPixel(waypoints[i - 1].lat, waypoints[i - 1].lng);
      const p2 = latLngToPixel(waypoints[i].lat, waypoints[i].lng);
      if (p1 && p2) {
        length += Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
      }
    }
    return length;
  }, [latLngToPixel]);

  // Get point along path at given progress (0-1)
  const getPointAtProgress = useCallback((waypoints: Waypoint[], progress: number): { x: number; y: number; segmentIndex: number } | null => {
    if (waypoints.length < 2) return null;

    const totalLength = calculatePathLength(waypoints);
    const targetLength = totalLength * Math.max(0, Math.min(1, progress));

    let accumulatedLength = 0;
    for (let i = 1; i < waypoints.length; i++) {
      const p1 = latLngToPixel(waypoints[i - 1].lat, waypoints[i - 1].lng);
      const p2 = latLngToPixel(waypoints[i].lat, waypoints[i].lng);
      if (!p1 || !p2) continue;

      const segmentLength = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
      
      if (accumulatedLength + segmentLength >= targetLength) {
        const segmentProgress = (targetLength - accumulatedLength) / segmentLength;
        return {
          x: p1.x + (p2.x - p1.x) * segmentProgress,
          y: p1.y + (p2.y - p1.y) * segmentProgress,
          segmentIndex: i - 1,
        };
      }
      accumulatedLength += segmentLength;
    }

    // Return last point
    const lastPoint = latLngToPixel(waypoints[waypoints.length - 1].lat, waypoints[waypoints.length - 1].lng);
    return lastPoint ? { ...lastPoint, segmentIndex: waypoints.length - 2 } : null;
  }, [calculatePathLength, latLngToPixel]);

  // Draw laser trail with glow
  const drawLaserTrail = useCallback((
    ctx: CanvasRenderingContext2D,
    waypoints: Waypoint[],
    progress: number,
    color: string,
    opacity: number = 1
  ) => {
    if (waypoints.length < 2 || progress <= 0) return;

    const glowSize = darkMode ? ANIMATION_SETTINGS.glowIntensity.dark : ANIMATION_SETTINGS.glowIntensity.light;
    
    // Draw glow layers (outer to inner)
    const glowLayers = [
      { blur: glowSize, width: ANIMATION_SETTINGS.coreWidth + 16, alpha: 0.15 * opacity },
      { blur: glowSize * 0.7, width: ANIMATION_SETTINGS.coreWidth + 10, alpha: 0.3 * opacity },
      { blur: glowSize * 0.4, width: ANIMATION_SETTINGS.coreWidth + 4, alpha: 0.5 * opacity },
    ];

    for (const layer of glowLayers) {
      ctx.save();
      ctx.filter = `blur(${layer.blur}px)`;
      ctx.strokeStyle = color;
      ctx.globalAlpha = layer.alpha;
      ctx.lineWidth = layer.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      let started = false;

      // Draw path up to current progress
      const trailStart = Math.max(0, progress - ANIMATION_SETTINGS.trailLength);
      
      for (let p = trailStart; p <= progress; p += 0.005) {
        const point = getPointAtProgress(waypoints, p);
        if (point) {
          if (!started) {
            ctx.moveTo(point.x, point.y);
            started = true;
          } else {
            ctx.lineTo(point.x, point.y);
          }
        }
      }

      ctx.stroke();
      ctx.restore();
    }

    // Draw core line
    ctx.save();
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.9 * opacity;
    ctx.lineWidth = ANIMATION_SETTINGS.coreWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = color;
    ctx.shadowBlur = glowSize * 0.5;

    ctx.beginPath();
    let started = false;
    const trailStart = Math.max(0, progress - ANIMATION_SETTINGS.trailLength);
    
    for (let p = trailStart; p <= progress; p += 0.005) {
      const point = getPointAtProgress(waypoints, p);
      if (point) {
        if (!started) {
          ctx.moveTo(point.x, point.y);
          started = true;
        } else {
          ctx.lineTo(point.x, point.y);
        }
      }
    }

    ctx.stroke();
    ctx.restore();

    // Draw laser head (bright point at front)
    const headPoint = getPointAtProgress(waypoints, progress);
    if (headPoint) {
      ctx.save();
      const gradient = ctx.createRadialGradient(
        headPoint.x, headPoint.y, 0,
        headPoint.x, headPoint.y, 12
      );
      gradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 * opacity})`);
      gradient.addColorStop(0.3, color);
      gradient.addColorStop(1, 'transparent');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(headPoint.x, headPoint.y, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }, [darkMode, getPointAtProgress]);

  // Draw waypoint markers
  const drawWaypoints = useCallback((
    ctx: CanvasRenderingContext2D,
    waypoints: Waypoint[],
    color: string,
    activatedWaypoints: Set<number>,
    waypointPulses: Map<number, number>,
    currentTime: number,
    opacity: number = 1
  ) => {
    waypoints.forEach((wp, index) => {
      const point = latLngToPixel(wp.lat, wp.lng);
      if (!point) return;

      const isActivated = activatedWaypoints.has(index);
      const pulseStartTime = waypointPulses.get(index);
      
      let scale = 1;
      let pulseOpacity = 0;
      
      if (pulseStartTime) {
        const elapsed = currentTime - pulseStartTime;
        if (elapsed < ANIMATION_SETTINGS.waypointPulseDuration) {
          const pulseProgress = elapsed / ANIMATION_SETTINGS.waypointPulseDuration;
          scale = 1 + (ANIMATION_SETTINGS.waypointPulseScale - 1) * Math.sin(pulseProgress * Math.PI);
          pulseOpacity = (1 - pulseProgress) * 0.5;
        }
      }

      const baseSize = index === 0 || index === waypoints.length - 1 ? 8 : 5;
      const size = baseSize * scale;

      // Outer glow for activated waypoints
      if (isActivated || pulseOpacity > 0) {
        ctx.save();
        ctx.globalAlpha = (isActivated ? 0.3 : 0) + pulseOpacity;
        ctx.fillStyle = color;
        ctx.filter = `blur(${darkMode ? 8 : 5}px)`;
        ctx.beginPath();
        ctx.arc(point.x, point.y, size * 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Main waypoint dot
      ctx.save();
      ctx.globalAlpha = (isActivated ? 0.9 : 0.4) * opacity;
      ctx.fillStyle = isActivated ? color : (darkMode ? '#64748b' : '#94a3b8');
      ctx.beginPath();
      ctx.arc(point.x, point.y, size, 0, Math.PI * 2);
      ctx.fill();

      // Inner highlight
      if (isActivated) {
        ctx.globalAlpha = 0.8 * opacity;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(point.x, point.y, size * 0.4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    });
  }, [darkMode, latLngToPixel]);

  // Draw distance label at destination when laser arrives
  const drawDistanceLabel = useCallback((
    ctx: CanvasRenderingContext2D,
    waypoints: Waypoint[],
    distanceMiles: number,
    routeName: string,
    color: string,
    opacity: number = 1,
    verticalOffset: number = 0, // Offset for stacking multiple labels
    routeId?: string // For registering click hit box
  ) => {
    if (waypoints.length < 2) return;
    
    // Get destination point (last waypoint)
    const destWp = waypoints[waypoints.length - 1];
    const destPoint = latLngToPixel(destWp.lat, destWp.lng);
    if (!destPoint) return;

    const glowSize = darkMode ? 15 : 10;
    const distanceText = `${distanceMiles.toFixed(2)} mi`;
    
    // Position label above the destination point, with vertical offset for stacking
    const baseOffset = 35;
    const stackSpacing = 45; // Space between stacked labels
    const labelY = destPoint.y - baseOffset - (verticalOffset * stackSpacing);
    const labelX = destPoint.x;

    ctx.save();
    
    // Draw glowing background pill
    ctx.font = 'bold 16px "Google Sans Flex", system-ui, sans-serif';
    const textWidth = ctx.measureText(distanceText).width;
    const pillWidth = textWidth + 24;
    const pillHeight = 28;
    const pillX = labelX - pillWidth / 2;
    const pillY = labelY - pillHeight / 2;

    // Outer glow
    ctx.globalAlpha = 0.4 * opacity;
    ctx.filter = `blur(${glowSize}px)`;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(pillX - 4, pillY - 4, pillWidth + 8, pillHeight + 8, 16);
    ctx.fill();

    // Background pill
    ctx.filter = 'none';
    ctx.globalAlpha = 0.95 * opacity;
    ctx.fillStyle = darkMode ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.95)';
    ctx.beginPath();
    ctx.roundRect(pillX, pillY, pillWidth, pillHeight, 14);
    ctx.fill();

    // Border glow
    ctx.globalAlpha = 0.8 * opacity;
    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.roundRect(pillX, pillY, pillWidth, pillHeight, 14);
    ctx.stroke();

    // Distance text
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1 * opacity;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(distanceText, labelX, labelY);

    // Draw route name below distance (smaller)
    if (routeName) {
      ctx.font = '11px "Google Sans Flex", system-ui, sans-serif';
      ctx.globalAlpha = 0.7 * opacity;
      ctx.fillStyle = darkMode ? '#94a3b8' : '#64748b';
      
      // Truncate long names
      const maxNameWidth = 100;
      let displayName = routeName;
      while (ctx.measureText(displayName).width > maxNameWidth && displayName.length > 10) {
        displayName = displayName.slice(0, -4) + '...';
      }
      ctx.fillText(displayName, labelX, labelY + 22);
    }

    ctx.restore();
    
    // Register hit box for click detection (only if routeId provided)
    if (routeId) {
      labelHitBoxesRef.current.push({
        routeId,
        x: pillX,
        y: pillY,
        width: pillWidth,
        height: pillHeight + 25, // Include route name area
      });
    }
  }, [darkMode, latLngToPixel]);

  // Main animation loop
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !map || !visible || routes.length === 0) {
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    // Resize canvas to match map container
    const mapDiv = map.getDiv();
    if (canvas.width !== mapDiv.clientWidth || canvas.height !== mapDiv.clientHeight) {
      canvas.width = mapDiv.clientWidth;
      canvas.height = mapDiv.clientHeight;
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Clear label hit boxes for this frame
    labelHitBoxesRef.current = [];

    const currentTime = performance.now();

    // Process routes once per frame
    const processedRoutes = processRoutes(routes);
    
    // Store for selection lookup
    processedRoutesRef.current = processedRoutes;

    // Assign colors to routes (handles crossing detection)
    assignRouteColors(processedRoutes);
    
    // RACE MODE: Calculate shortest route distance for speed normalization
    const shortestDistance = Math.min(...processedRoutes.map(r => r.distanceMiles));
    shortestDistanceRef.current = shortestDistance;
    
    // Check if any route just finished fading - use this to sync all routes for race mode
    let shouldSyncStart = false;
    processedRoutes.forEach((route) => {
      const state = routeStatesRef.current.get(route.id);
      if (state && state.phase === 'fading' && state.fadeProgress >= 1) {
        shouldSyncStart = true;
      }
    });
    
    // If any route just completed, reset ALL routes to start together (race mode)
    if (shouldSyncStart) {
      processedRoutes.forEach((route) => {
        const state = routeStatesRef.current.get(route.id);
        if (state) {
          state.phase = 'tracing';
          state.progress = 0;
          state.fadeProgress = 0;
          state.activatedWaypoints.clear();
          state.waypointPulses.clear();
        }
      });
    }

    // Track destinations that will show labels this frame (for stacking)
    // Key: "lat,lng" rounded to 3 decimals for wider grouping (~100m radius)
    const destinationLabels = new Map<string, string[]>();
    
    // First pass: identify which routes are showing labels and where
    processedRoutes.forEach((route) => {
      if (route.waypoints.length < 2) return;
      const state = routeStatesRef.current.get(route.id);
      if (state && (state.phase === 'pausing' || state.phase === 'fading')) {
        const destWp = route.waypoints[route.waypoints.length - 1];
        // Round to 3 decimal places to group nearby destinations (~100m radius)
        const destKey = `${destWp.lat.toFixed(3)},${destWp.lng.toFixed(3)}`;
        const existing = destinationLabels.get(destKey) || [];
        existing.push(route.id);
        destinationLabels.set(destKey, existing);
      }
    });

    // Animate each route
    processedRoutes.forEach((route) => {
      if (route.waypoints.length < 2) return;

      // Get or create animation state
      let state = routeStatesRef.current.get(route.id);
      if (!state) {
        state = {
          progress: 0,
          phase: 'tracing',
          pauseEndTime: 0,
          fadeProgress: 0,
          activatedWaypoints: new Set(),
          waypointPulses: new Map(),
        };
        routeStatesRef.current.set(route.id, state);
      }

      // Use route-specific color (crossing or surface type)
      const color = getRouteColor(route.id, route.surfaceType);
      
      // RACE MODE: All lasers move at similar visual speeds
      // But shorter routes = fewer pixels to travel = finish faster
      // Use a consistent base speed with minor adjustment for very long routes
      const longestDistance = Math.max(...processedRoutes.map(r => r.distanceMiles));
      const distanceRatio = route.distanceMiles / longestDistance; // 0 to 1
      // Longer routes get slightly faster to prevent them taking forever
      // But the main "race" effect comes from shorter routes having less ground to cover
      const speed = ANIMATION_SETTINGS.baseSpeed * (1 + (1 - distanceRatio) * 0.3); // Shorter routes 1.3x, longest 1x

      // Update animation based on phase
      switch (state.phase) {
        case 'tracing':
          state.progress += speed;
          
          // Check for waypoint activations
          route.waypoints.forEach((_wp, index) => {
            if (state!.activatedWaypoints.has(index)) return;
            
            const waypointProgress = index / (route.waypoints.length - 1);
            if (state!.progress >= waypointProgress) {
              state!.activatedWaypoints.add(index);
              state!.waypointPulses.set(index, currentTime);
            }
          });

          if (state.progress >= 1) {
            state.phase = 'pausing';
            state.pauseEndTime = currentTime + 
              ANIMATION_SETTINGS.pauseMin + 
              Math.random() * (ANIMATION_SETTINGS.pauseMax - ANIMATION_SETTINGS.pauseMin);
          }
          break;

        case 'pausing':
          if (currentTime >= state.pauseEndTime) {
            state.phase = 'fading';
            state.fadeProgress = 0;
          }
          break;

        case 'fading':
          state.fadeProgress += 1000 / 60 / ANIMATION_SETTINGS.fadeOutDuration;
          if (state.fadeProgress >= 1) {
            // DON'T reset here - let race mode sync handle it
            // Just stay at completed state until sync triggers
          }
          break;
      }

      // Calculate opacity based on phase and isolation state
      let opacity = 1;
      if (state.phase === 'fading') {
        opacity = 1 - state.fadeProgress;
      }
      
      // Apply dimmed opacity for non-isolated routes when one is focused
      const isIsolated = isolatedRouteId === route.id;
      const hasIsolation = isolatedRouteId !== null;
      if (hasIsolation && !isIsolated) {
        opacity *= ANIMATION_SETTINGS.dimmedOpacity;
      }
      
      // Auto-reset isolation when isolated route completes its loop (fading complete)
      if (isIsolated && state.phase === 'fading' && state.fadeProgress >= 1) {
        // Will be reset on next frame via the setIsolatedRouteId call below
        setTimeout(() => setIsolatedRouteId(null), 0);
      }

      // Draw the route
      drawWaypoints(
        ctx,
        route.waypoints,
        color,
        state.activatedWaypoints,
        state.waypointPulses,
        currentTime,
        opacity
      );
      
      drawLaserTrail(
        ctx,
        route.waypoints,
        state.progress,
        color,
        opacity
      );

      // Draw distance label when laser reaches destination (during pause and fade phases)
      if (state.phase === 'pausing' || state.phase === 'fading') {
        // Calculate vertical offset for stacking labels at same destination
        const destWp = route.waypoints[route.waypoints.length - 1];
        const destKey = `${destWp.lat.toFixed(3)},${destWp.lng.toFixed(3)}`;
        const labelsAtDest = destinationLabels.get(destKey) || [];
        const labelIndex = labelsAtDest.indexOf(route.id);
        const verticalOffset = labelIndex >= 0 ? labelIndex : 0;
        
        drawDistanceLabel(
          ctx,
          route.waypoints,
          route.distanceMiles,
          route.name,
          color,
          opacity,
          verticalOffset,
          route.id // Pass route ID for hit box registration
        );
      }
    });

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [map, routes, visible, getRouteColor, drawLaserTrail, drawWaypoints, drawDistanceLabel, processRoutes, assignRouteColors, isolatedRouteId]);

  // Start/stop animation
  useEffect(() => {
    if (visible && routes.length > 0) {
      animate();
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // Clear canvas when hidden
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      // Reset animation states and colors
      routeStatesRef.current.clear();
      routeColorsRef.current.clear();
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [visible, routes, animate]);

  // Re-render on map move/zoom
  useEffect(() => {
    if (!map) return;

    const listeners = [
      map.addListener('bounds_changed', () => {
        // Animation will automatically pick up new bounds
      }),
      map.addListener('zoom_changed', () => {
        // Reset states on zoom to recalculate paths
        routeStatesRef.current.clear();
      }),
    ];

    return () => {
      listeners.forEach((listener) => google.maps.event.removeListener(listener));
    };
  }, [map]);

  if (!visible) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none', // Don't block map interactions (zoom, pan)
        zIndex: 1,
      }}
    >
      <canvas
        ref={canvasRef}
        onClick={(e) => {
          const canvas = canvasRef.current;
          if (!canvas) return;
          
          const rect = canvas.getBoundingClientRect();
          const scaleX = canvas.width / rect.width;
          const scaleY = canvas.height / rect.height;
          const x = (e.clientX - rect.left) * scaleX;
          const y = (e.clientY - rect.top) * scaleY;
          
          // Check if click is inside any label hit box
          for (const hitBox of labelHitBoxesRef.current) {
            if (
              x >= hitBox.x &&
              x <= hitBox.x + hitBox.width &&
              y >= hitBox.y &&
              y <= hitBox.y + hitBox.height
            ) {
              // Prevent map from receiving this click
              e.preventDefault();
              e.stopPropagation();
              
              console.log('[LaserRouteOverlay] HIT! Route:', hitBox.routeId);
              // Toggle isolation
              if (isolatedRouteId === hitBox.routeId) {
                setIsolatedRouteId(null);
              } else {
                setIsolatedRouteId(hitBox.routeId);
              }
              return;
            }
          }
          
          // Click outside any label - clear isolation if set
          if (isolatedRouteId) {
            setIsolatedRouteId(null);
          }
        }}
        onDoubleClick={(e) => {
          // Check if double-click is on a label - if so, prevent map zoom
          const canvas = canvasRef.current;
          if (!canvas) return;
          
          const rect = canvas.getBoundingClientRect();
          const scaleX = canvas.width / rect.width;
          const scaleY = canvas.height / rect.height;
          const x = (e.clientX - rect.left) * scaleX;
          const y = (e.clientY - rect.top) * scaleY;
          
          const isOnLabel = labelHitBoxesRef.current.some(
            hb => x >= hb.x && x <= hb.x + hb.width && y >= hb.y && y <= hb.y + hb.height
          );
          
          if (isOnLabel) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          pointerEvents: 'auto',
        }}
        onWheel={(e) => {
          // Always let wheel events through for map zoom
          e.currentTarget.style.pointerEvents = 'none';
          setTimeout(() => {
            if (canvasRef.current) canvasRef.current.style.pointerEvents = 'auto';
          }, 50);
        }}
        onMouseDown={(e) => {
          // Check if mousedown is on a label - if not, let map handle it
          const canvas = e.currentTarget;
          const rect = canvas.getBoundingClientRect();
          const scaleX = canvas.width / rect.width;
          const scaleY = canvas.height / rect.height;
          const x = (e.clientX - rect.left) * scaleX;
          const y = (e.clientY - rect.top) * scaleY;
          
          const isOnLabel = labelHitBoxesRef.current.some(
            hb => x >= hb.x && x <= hb.x + hb.width && y >= hb.y && y <= hb.y + hb.height
          );
          
          if (isOnLabel) {
            // Keep pointer events and prevent map pan
            e.preventDefault();
            e.stopPropagation();
          } else {
            // Let map handle this interaction
            canvas.style.pointerEvents = 'none';
            setTimeout(() => {
              if (canvasRef.current) canvasRef.current.style.pointerEvents = 'auto';
            }, 100);
          }
        }}
      />
    </Box>
  );
};

export default LaserRouteOverlay;
